import Http from '../../../../network/Http'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {NatGetOwnershipTransferList, SearchCondition} from './NatGetOwnershipTransferList'
import {ContentType} from '../../../../network/Constants'
import {NatGetOwnershipTransfer} from './NatGetOwnershipTransfer'
import {NatPostOwnershipTransfers, NatPostOwnershipTransfersParams} from './NatPostOwnershipTransfers'
import {NatPostIntermediaryRequest, NatPostIntermediaryResponse} from './NatPostIntermediaryRequest'
import {NatGetOwnershipTransferIntermediaryResponse} from './NatGetOwnershipTransferIntermediary'
import {NatPostOldMemberRequest, NatPostOldMemberResponse} from './NatPostOldMemberRequest'
import {NatGetOwnershipTransferOldMember} from './NatGetOwnershipTransferOldMember'
import {
    NatPutNewMemberRequest,
    NatPutOwnershipTransferNewMemberOutlineRequest,
    NatPutOwnershipTransferNewMemberResponse
} from './NatPutNewMemberRequest';
import {NatGetOwnershipTransferNewMemberResponse} from './NatGetOwnershipTransferNewMember';
import {OwnershipTransferSubmitStatus,} from './NatGetOwnershipTransferSubmitStatus';
import {NatGetAccessKeyByLoginUser} from './NatGetAccessKeyByLoginUser'
import {NatGetOwnershipTransferManualUrl} from './NatGetOwnershipTransferManualUrl'

export class NatOwnershipTransfersRepo {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async issueOwnershipTransfers(
        condominiumCd: string,
        request: NatPostOwnershipTransfersParams,
    ): Promise<NatPostOwnershipTransfers> {
        return await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/applications/ownership-transfers`,
            {
                ridgeCd: request.ridgeCd,
                roomNumber: request.roomNumber,
                intermediaryName: request.intermediaryName,
            },
            ContentType.JSON
        )
    }

    async getOwnershipTransferList(
        searchCondition: SearchCondition,
        searchConditionSortInfo: SortOrder[],
    ): Promise<NatGetOwnershipTransferList> {
        const sortInfos: string[] = []
        if (searchConditionSortInfo) {
            searchConditionSortInfo.forEach((sortInfo) => {
                sortInfos.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }

        return await this.http.post(
            `/api/admin/condominiums/applications/ownership-transferList`,
            {
                condominiumGroupId: searchCondition.condominiumGroupId || null,
                condominiumCd: searchCondition.condominiumCd || null,
                condominiumName: searchCondition.condominiumName || null,
                ridgeName: searchCondition.ridgeName || null,
                roomNumber: searchCondition.roomNumber || null,
                applyStatus: searchCondition.applyStatus || null,
                applyId: searchCondition.applyId || null,
                completeStartDate: searchCondition.completeStartDate || null,
                completeEndDate: searchCondition.completeEndDate || null,
                pageNum: searchCondition.pageNum ?? null,
                page: searchCondition.page ?? null,
                sortInfo: sortInfos,
            },
            ContentType.JSON
        )
    }

    async getOwnershipTransfer(condominiumCd: string, applyId: string):
        Promise<NatGetOwnershipTransfer> {
        return await this.http.get(
            `/api/admin/condominiums/${condominiumCd}/applications/ownership-transfers/${applyId}`
        )
    }

    async applyOwnershipTransferIntermediary(
        condominiumCd: string,
        request: NatPostIntermediaryRequest
    ): Promise<NatPostIntermediaryResponse> {
        return await this.http.post(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/intermediary`,
            request,
            ContentType.JSON
        )
    }

    async getOwnershipTransferIntermediary(condominiumCd: string, applyId: string):
        Promise<NatGetOwnershipTransferIntermediaryResponse> {
        return await this.http.get(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/intermediary/${applyId}`
        )
    }

    async getOwnershipTransfersSubmitStatus(condominiumCd: string):
        Promise<OwnershipTransferSubmitStatus> {
        return await this.http.get(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/submit-status/`
        )
    }

    async applyOwnershipTransferOldMember(
        condominiumCd: string,
        request: NatPostOldMemberRequest
    ): Promise<NatPostOldMemberResponse> {
        return await this.http.post(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/old-member`,
            request,
            ContentType.JSON
        )
    }
    async getOwnershipTransferOldMember(condominiumCd: string, applyId: string):
        Promise<NatGetOwnershipTransferOldMember> {
        return await this.http.get(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/old-member/${applyId}`
        )
    }

    async applyOwnershipTransferNewMemberOutline(
        condominiumCd: string,
        request: NatPutOwnershipTransferNewMemberOutlineRequest
    ): Promise<NatPutOwnershipTransferNewMemberResponse> {
        return await this.http.post(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/new-member/outline`,
            request,
            ContentType.JSON
        )
    }

    async applyOwnershipTransferNewMember(
        condominiumCd: string,
        request: NatPutNewMemberRequest
    ): Promise<NatPutOwnershipTransferNewMemberResponse> {
        return await this.http.post(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/new-member`,
            request,
            ContentType.JSON
        )
    }

    async getOwnershipTransferNewMember(condominiumCd: string, applyId: string):
        Promise<NatGetOwnershipTransferNewMemberResponse> {
        return await this.http.get(
            `/api/guest/condominiums/${condominiumCd}/applications/ownership-transfers/new-member/${applyId}`
        )
    }

    // 廃止予定、今後はgetAccessKeyAuthedGuestを使用すること
    async getAccessKeyByLoginUser(condominiumCd: string):
        Promise<NatGetAccessKeyByLoginUser> {
        return await this.http.get(
            `/api/guest/applications/ownership-transfers/accessKey/authed`
        )
    }

    async getAccessKeyAuthedGuest():
        Promise<NatGetAccessKeyByLoginUser> {
        return await this.http.get(
            `/api/guest/applications/ownership-transfers/accessKey/authed`
        )
    }

    async getOwnershipTransferManualUrl(accessKey: string):
        Promise<NatGetOwnershipTransferManualUrl> {
        return await this.http.get(
            `/api/guest/applications/ownership-transfers/manual?accessKey=${accessKey}`
        )
    }

    exportOwnershipTransferListByCsvData(searchCondition: SearchCondition): string {
        // 今後、リクエストに個人情報を含む場合はPOST化を検討する
        const searchParams: string[] = []
        if (searchCondition.condominiumGroupId) {
            searchParams.push(`condominiumGroupId=${searchCondition.condominiumGroupId}`)
        }
        if (searchCondition.condominiumCd) {
            searchParams.push(`condominiumCd=${searchCondition.condominiumCd}`)
        }
        if (searchCondition.condominiumName) {
            searchParams.push(`condominiumName=${searchCondition.condominiumName}`)
        }
        if (searchCondition.ridgeName) {
            searchParams.push(`ridgeName=${searchCondition.ridgeName}`)
        }
        if (searchCondition.roomNumber) {
            searchParams.push(`roomNumber=${searchCondition.roomNumber}`)
        }
        if (searchCondition.applyStatus) {
            searchParams.push(`applyStatus=${searchCondition.applyStatus}`)
        }
        if (searchCondition.applyId) {
            searchParams.push(`applyId=${searchCondition.applyId}`)
        }
        if (searchCondition.completeStartDate) {
            searchParams.push(`completeStartDate=${searchCondition.completeStartDate}`)
        }
        if (searchCondition.completeEndDate) {
            searchParams.push(`completeEndDate=${searchCondition.completeEndDate}`)
        }
        const searchParamsText: string = searchParams.join('&')
        return `/api/admin/applications/ownership-transferList/export?${searchParamsText}`
    }
}
